
import { initializeApp, type FirebaseApp } from 'firebase/app'
import '../config/boot'
import { getAuth, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getStorage, type FirebaseStorage as CloudStorage } from 'firebase/storage'
import { getMessaging, type Messaging } from 'firebase/messaging'
import { USE_MOCK } from '../config/env'

// ============================================================================
// CONFIGURAÇÃO DO FIREBASE - LEIA COM ATENÇÃO!
// ============================================================================
// PARA TODOS OS AMBIENTES: Use variáveis de ambiente
// 
// 1. Produção (Netlify): Defina no painel do Netlify (Environment variables)
// 2. Desenvolvimento local: Crie arquivo .env.local (veja .env.example)
// 
// NUNCA COMMITE CHAVES REAIS NO REPOSITÓRIO!
// ============================================================================

// Helper para obter variáveis de ambiente do Vite
// Vite expõe variáveis com prefixo VITE_ através de import.meta.env
const getEnv = (key: string): string => {
  if (import.meta.env && import.meta.env[key]) {
    const value = import.meta.env[key] as string
    // Ignorar valores de placeholder
    if (value && 
        !value.includes('your_api_key') && 
        !value.includes('SUA_CHAVE_API_REAL_AQUI') &&
        value.trim().length > 0) {
      return value
    }
  }
  return ''
}

// Configuração do Firebase: usa APENAS variáveis de ambiente
// Para desenvolvimento local, crie um arquivo .env.local com suas credenciais
// Veja .env.example para o formato
const firebaseConfig = {
  apiKey: getEnv('VITE_FIREBASE_API_KEY'),
  authDomain: getEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: getEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: getEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: getEnv('VITE_FIREBASE_APP_ID'),
  measurementId: getEnv('VITE_FIREBASE_MEASUREMENT_ID'),
}

// Verificar se estamos em ambiente de desenvolvimento local
const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname === '127.0.0.1')

// Detectar mock mode (credenciais ausentes ou placeholders) com toggle via env
let isMock = USE_MOCK || !firebaseConfig.apiKey || 
             firebaseConfig.apiKey.includes('your_api_key') || 
             firebaseConfig.apiKey.includes('SUA_CHAVE_API_REAL_AQUI') ||
             !firebaseConfig.authDomain || 
             !firebaseConfig.projectId

// Log para debugging
console.log('Firebase config check:', { 
  hasApiKey: !!firebaseConfig.apiKey,
  apiKeyLength: firebaseConfig.apiKey?.length,
  apiKeyEndsWith: firebaseConfig.apiKey?.slice(-4),
  isMock,
  isLocalhost,
  projectId: firebaseConfig.projectId,
  usingEnvVars: !!getEnv('VITE_FIREBASE_API_KEY')
})
if (isMock) {
  console.warn('⚠️ Firebase está em MOCK MODE. Para usar Firebase real, configure as variáveis de ambiente no arquivo .env.local:')
  console.warn('VITE_FIREBASE_API_KEY=your_api_key')
  console.warn('VITE_FIREBASE_AUTH_DOMAIN=your_project.firebaseapp.com')
  console.warn('VITE_FIREBASE_PROJECT_ID=your_project_id')
  console.warn('...')
  console.warn('Veja .env.example para mais detalhes.')
}

// Aviso em produção se estiver em mock mode
if (import.meta.env.PROD && isMock) {
  console.warn('⚠️ Firebase em MOCK MODE na produção! Configure as variáveis de ambiente VITE_FIREBASE_* no Netlify Dashboard.')
}

// Initialize Firebase only if not in mock mode
let app: FirebaseApp | null = null
let auth: Auth
let db: Firestore  
let storage: CloudStorage
let messaging: Messaging | null = null

if (!isMock) {
  try {
    app = initializeApp(firebaseConfig)
    auth = getAuth(app)
    db = getFirestore(app)
    
    // Em localhost, não inicializar storage para evitar erros de CORS
    if (!isLocalhost) {
      storage = getStorage(app)
      console.log('Storage initialized for non-localhost environment')
    } else {
      console.warn('Localhost detected: Storage not initialized to avoid CORS issues')
      storage = {} as unknown as CloudStorage
    }
    
    // Inicializar Messaging para notificações push
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        messaging = getMessaging(app)
        console.log('✅ Firebase Messaging initialized')
      } catch (e) {
        console.warn('⚠️ Firebase Messaging not available:', e)
      }
    }
    
    console.log('✅ Firebase initialized with real project:', firebaseConfig.projectId)
  } catch (error) {
    console.error('❌ Firebase initialization error:', error)
    // Fallback para mock mode em caso de erro
    isMock = true
  }
}

// Se estiver em mock mode, criar objetos simulados
if (isMock) {
  console.warn('⚠️ Firebase running in mock mode - using simulated authentication')
  
  auth = {
    currentUser: null,
    onAuthStateChanged: () => () => {},
    signInWithEmailAndPassword: async () => ({ user: null }),
    createUserWithEmailAndPassword: async () => ({ user: null }),
    signOut: async () => {},
    sendPasswordResetEmail: async () => {},
    updateProfile: async () => {},
  } as unknown as Auth
  
  db = {} as unknown as Firestore
  storage = {} as unknown as CloudStorage
  
  // Criar app mock apenas para compatibilidade
  app = null as unknown as FirebaseApp
}

export { auth, db, storage, messaging, isMock, firebaseConfig }
export default app
